import { useEffect, useState } from "react";
import { CreditPlan } from "./types";
import {
  getCreditPlans,
  getPurchaseEnables,
  PurchaseEnablesResponse,
  UpdatePurchaseEnable,
  updatePurchaseSettings,
} from "apis/creditService";
import { loadStripe } from "@stripe/stripe-js";
import PlanSlider from "./Components/PlanSlider";
import AutoPurchaseSettings from "./Components/AutoPurchaseSettings";
import MonthlyPurchaseSettings from "./Components/MonthlyPurchaseSettings";
import PaymentForm from "./Components/PaymentForm";
import ConfirmationModal from "./Components/ConfermationModal";
import TermsModal from "components/TermsModal";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "");

interface CreditPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPaymentComplete: () => void;
}

const CreditPurchaseModal = ({ isOpen, onClose, onPaymentComplete }: CreditPurchaseModalProps) => {
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [plans, setPlans] = useState<CreditPlan[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAutoPurchaseEnabled, setIsAutoPurchaseEnabled] = useState<boolean>(false);
  const [isAutoPurchaseSaved, setIsAutoPurchaseSaved] = useState<boolean>(false);
  const [autoPurchaseCreditPlanId, setAutoPurchaseCreditPlanId] = useState<number>(5);
  const [autoPurchaseThreshold, setAutoPurchaseThreshold] = useState<number>(5);
  const [isMonthlyPurchaseEnabled, setIsMonthlyPurchaseEnabled] = useState<boolean>(false);
  const [isMonthlyPurchaseSaved, setIsMonthlyPurchaseSaved] = useState<boolean>(false);
  const [monthlyPurchaseCreditPlanId, setMonthlyPurchaseCreditPlanId] = useState<number>(5);
  const [purchaseMode, setPurchaseMode] = useState<"auto" | "monthly" | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingMode, setPendingMode] = useState<"auto" | "monthly" | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const plansResponse = await getCreditPlans();
        const activePlans = plansResponse
          .filter((plan: CreditPlan) => plan.isActive)
          .map((plan: CreditPlan) => ({
            ...plan,
            id: Number(plan.id),
          }));

        if (activePlans.length > 0) {
          const sortedPlans = activePlans.sort((a, b) => (a.creditNumber ?? 0) - (b.creditNumber ?? 0));
          setPlans(sortedPlans);
          const fiveCreditsIndex = sortedPlans.findIndex((plan) => plan.creditNumber === 5);
          setSelectedIndex(fiveCreditsIndex >= 0 ? fiveCreditsIndex : 0);
        } else {
          setError("No active credit plans available.");
        }

        const enablesResponse = await getPurchaseEnables();
        if (enablesResponse.isAutoPurchaseEnabled) {
          setPurchaseMode("auto");
          setIsAutoPurchaseSaved(true);
          setIsAutoPurchaseEnabled(true);
          setIsMonthlyPurchaseSaved(false);
          setIsMonthlyPurchaseEnabled(false);
          setAutoPurchaseCreditPlanId(enablesResponse.autoPurchaseCreditPlanId);
          setAutoPurchaseThreshold(enablesResponse.autoPurchaseCreditLimit);
        } else if (enablesResponse.isMonthlyPurchaseEnabled) {
          setPurchaseMode("monthly");
          setIsMonthlyPurchaseSaved(true);
          setIsMonthlyPurchaseEnabled(true);
          setIsAutoPurchaseSaved(false);
          setIsAutoPurchaseEnabled(false);
          setMonthlyPurchaseCreditPlanId(enablesResponse.monthlyCreditPlanId || 5);
        } else {
          setPurchaseMode(null);
          setIsAutoPurchaseSaved(false);
          setIsAutoPurchaseEnabled(false);
          setIsMonthlyPurchaseSaved(false);
          setIsMonthlyPurchaseEnabled(false);
        }
      } catch (err) {
        setError("Failed to load credit plans.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  // Set plan ID to first plan when enabling purchase mode and plans are available
  useEffect(() => {
    if (plans.length > 0) {
      if (isAutoPurchaseEnabled && !isAutoPurchaseSaved && autoPurchaseCreditPlanId <= 0) {
        setAutoPurchaseCreditPlanId(plans[0].id);
      }
      if (isMonthlyPurchaseEnabled && !isMonthlyPurchaseSaved && monthlyPurchaseCreditPlanId <= 0) {
        setMonthlyPurchaseCreditPlanId(plans[0].id);
      }
    }
  }, [
    plans,
    isAutoPurchaseEnabled,
    isMonthlyPurchaseEnabled,
    isAutoPurchaseSaved,
    isMonthlyPurchaseSaved,
    autoPurchaseCreditPlanId,
    monthlyPurchaseCreditPlanId,
  ]);

  const handleTogglePurchaseMode = (mode: "auto" | "monthly") => {
    if (purchaseMode && purchaseMode !== mode) {
      setPendingMode(mode);
      setShowConfirmModal(true);
    } else {
      if (purchaseMode === mode) {
        setPurchaseMode(null);
        if (mode === "auto") {
          setIsAutoPurchaseEnabled(false);
          setIsAutoPurchaseSaved(false);
        } else {
          setIsMonthlyPurchaseEnabled(false);
          setIsMonthlyPurchaseSaved(false);
        }
      } else {
        setPurchaseMode(mode);
        if (mode === "auto") {
          setIsAutoPurchaseEnabled(true);
          setIsMonthlyPurchaseEnabled(false);
          setIsMonthlyPurchaseSaved(false);
        } else {
          setIsMonthlyPurchaseEnabled(true);
          setIsAutoPurchaseEnabled(false);
          setIsAutoPurchaseSaved(false);
          // Set monthlyPurchaseCreditPlanId to first plan's ID if plans are available
          if (plans.length > 0) {
            setMonthlyPurchaseCreditPlanId(plans[0].id);
          }
        }
      }
    }
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
    setPendingMode(null);
  };

  const handleCloseEverything = () => {
    setShowPaymentForm(false);
    onClose();
  };

  const handleConfirmModalConfirm = () => {
    if (pendingMode) {
      setPurchaseMode(pendingMode);
      if (pendingMode === "auto") {
        setIsAutoPurchaseEnabled(true);
        setIsMonthlyPurchaseEnabled(false);
        setIsMonthlyPurchaseSaved(false);
        // Set autoPurchaseCreditPlanId to first plan's ID if plans are available
        if (plans.length > 0) {
          setAutoPurchaseCreditPlanId(plans[0].id);
        }
      } else {
        setIsMonthlyPurchaseEnabled(true);
        setIsAutoPurchaseEnabled(false);
        setIsAutoPurchaseSaved(false);
        // Set monthlyPurchaseCreditPlanId to first plan's ID if plans are available
        if (plans.length > 0) {
          setMonthlyPurchaseCreditPlanId(plans[0].id);
        }
      }
    }
    setShowConfirmModal(false);
    setPendingMode(null);
  };

  const handleAutoPurchaseChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>, field: "threshold" | "planId") => {
    const value = Number(e.target.value);
    if (value >= 0) {
      if (field === "threshold") {
        setAutoPurchaseThreshold(value);
      } else {
        setAutoPurchaseCreditPlanId(value);
      }
    }
  };

  const handleMonthlyPurchaseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    if (value >= 0) {
      setMonthlyPurchaseCreditPlanId(value);
    }
  };

  const handleSaveAutoPurchase = async () => {
    // Ensure autoPurchaseCreditPlanId is set to first plan's ID if it's 0 or invalid
    const validPlanId =
      autoPurchaseCreditPlanId > 0 && plans.some((p) => p.id === autoPurchaseCreditPlanId)
        ? autoPurchaseCreditPlanId
        : plans.length > 0
          ? plans[0].id
          : 0;

    if (validPlanId > 0 && autoPurchaseThreshold >= 0) {
      try {
        setIsSaving(true);
        const purchaseData: UpdatePurchaseEnable = {
          isAutoPurchaseEnabled: true,
          autoPurchaseCreditPlanId: validPlanId,
          autoPurchaseCreditLimit: autoPurchaseThreshold,
          isMonthlyPurchaseEnabled: false,
          monthlyPurchaseCreditPlanId: 0,
          currency: selectedPlan.amount.toFixed(1),
        };

        // Call the creditPurchase API
        await updatePurchaseSettings(purchaseData);

      } catch (err) {
        setError("An unexpected error occurred");
        console.error(err);
      } finally {
        setIsSaving(false);
      }
      setIsAutoPurchaseSaved(true);
      setIsAutoPurchaseEnabled(true);
      setIsMonthlyPurchaseEnabled(false);
      setIsMonthlyPurchaseSaved(false);
      setPurchaseMode("auto");
      setError(null);
    } else {
      setError("Please enter valid auto-purchase values.");
    }
  };

  const handleSaveMonthlyPurchase = async () => {
    // Ensure monthlyPurchaseCreditPlanId is set to first plan's ID if it's 0 or invalid
    const validPlanId =
      monthlyPurchaseCreditPlanId > 0 && plans.some((p) => p.id === monthlyPurchaseCreditPlanId)
        ? monthlyPurchaseCreditPlanId
        : plans.length > 0
          ? plans[0].id
          : 0;

    if (validPlanId > 0) {
      try {
        setIsSaving(true);
        const purchaseData: UpdatePurchaseEnable = {
          isAutoPurchaseEnabled: false,
          autoPurchaseCreditPlanId: 0,
          autoPurchaseCreditLimit: 0,
          isMonthlyPurchaseEnabled: true,
          monthlyPurchaseCreditPlanId: validPlanId,
          currency: selectedPlan.amount.toFixed(1),
        };

        // Call the creditPurchase API
        await updatePurchaseSettings(purchaseData);

      } catch (err) {
        setError("An unexpected error occurred");
        console.error(err);
      } finally {
        setIsSaving(false);
      }
      setIsMonthlyPurchaseSaved(true);
      setIsMonthlyPurchaseEnabled(true);
      setIsAutoPurchaseEnabled(false);
      setIsAutoPurchaseSaved(false);
      setPurchaseMode("monthly");
      setError(null);
    } else {
      setError("Please enter a valid monthly purchase plan.");
    }
  };

  const selectedPlan = plans[selectedIndex] ?? null;

  return (
    <>
      <div
        className={`${isOpen && !showPaymentForm ? "flex" : "hidden"} overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-50 md:inset-0 justify-center items-center w-full`}
        tabIndex={-1}
        aria-hidden={!(isOpen && !showPaymentForm)}
      >
        {isOpen && !showPaymentForm && <div className="bg-gray-900/50 dark:bg-gray-900/80 fixed inset-0 z-40"></div>}
        <div className="relative p-4 w-full max-w-2xl z-50">
          <div className="relative bg-white rounded-lg shadow">
            <div className="flex items-center justify-between mx-6 py-4 rounded-t border-b border-lightgray-100">
              <p className="text-base font-semibold text-grease-100">Purchase Token</p>
              <button
                type="button"
                className="text-gray-600 bg-transparent rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center"
                onClick={onClose}
              >
                <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                  />
                </svg>
                <span className="sr-only">Close modal</span>
              </button>
            </div>
            <div className="px-6 py-4">
              {loading && <p className="text-sm text-gray-500">Loading plans...</p>}
              {error && <p className="text-sm text-red-500">{error}</p>}
              {!loading && !error && plans.length > 0 && selectedPlan && (
                <>
                  <PlanSlider plans={plans} selectedIndex={selectedIndex} setSelectedIndex={setSelectedIndex} selectedPlan={selectedPlan} />
                  <AutoPurchaseSettings
                    plans={plans}
                    isAutoPurchaseEnabled={isAutoPurchaseEnabled}
                    isAutoPurchaseSaved={isAutoPurchaseSaved}
                    autoPurchaseCreditPlanId={autoPurchaseCreditPlanId}
                    autoPurchaseThreshold={autoPurchaseThreshold}
                    isSaving={isSaving}
                    purchaseMode={purchaseMode}
                    handleTogglePurchaseMode={handleTogglePurchaseMode}
                    handleAutoPurchaseChange={handleAutoPurchaseChange}
                    handleSaveAutoPurchase={handleSaveAutoPurchase}
                    handleEditAutoPurchase={() => setIsAutoPurchaseSaved(false)}
                  />
                  <MonthlyPurchaseSettings
                    plans={plans}
                    isMonthlyPurchaseEnabled={isMonthlyPurchaseEnabled}
                    isMonthlyPurchaseSaved={isMonthlyPurchaseSaved}
                    monthlyPurchaseCreditPlanId={monthlyPurchaseCreditPlanId}
                    isSaving={isSaving}
                    purchaseMode={purchaseMode}
                    handleTogglePurchaseMode={handleTogglePurchaseMode}
                    handleMonthlyPurchaseChange={handleMonthlyPurchaseChange}
                    handleSaveMonthlyPurchase={handleSaveMonthlyPurchase}
                    handleEditMonthlyPurchase={() => setIsMonthlyPurchaseSaved(false)}
                  />
                  <div className="flex items-center justify-between gap-4 mt-4">
                    <div className="flex flex-col gap-2">
                      <p className="text-lightgray-200 text-sm">Total Amount</p>
                      {/* Show Discount Percentage only if a discount is present */}
                      {selectedPlan.discount > 0 && (
                        <p className="text-sm text-green-500 font-medium">Discount: {selectedPlan.discount}% OFF! 🏷️</p>
                      )}
                      <p className="text-lightgray-200 text-xs">(Taxes Included)</p>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      {/* Show Original Price if a discount is present */}
                      {selectedPlan.discount > 0 ? (
                        <>
                          <span className="text-sm text-lightgray-200 line-through">${selectedPlan.amount.toFixed(1)}</span>
                          {/* Show Discounted Price */}
                          <span className="text-2xl text-grease-100 font-semibold">${selectedPlan.discountedAmount.toFixed(1)}</span>
                        </>
                      ) : (
                        // Show Normal Price if no discount
                        <span className="text-2xl text-grease-100 font-semibold">${selectedPlan.amount.toFixed(1)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center mt-6">
                    <input
                      id="checked-checkbox"
                      type="checkbox"
                      checked={termsAccepted}
                      onClick={(e) => {
                        if (!termsAccepted) {
                          e.preventDefault();
                          setIsTermsModalOpen(true);
                        }
                      }}
                      onChange={(e) => {
                        if (termsAccepted) {
                          setTermsAccepted(e.target.checked);
                        }
                      }}
                      className="w-4 h-4 text-light-yellow bg-gray-100 border-gray-300 rounded focus:ring-light-yellow cursor-pointer"
                    />
                    <label htmlFor="checked-checkbox" className="ml-2 text-xs font-medium text-gray-800">
                      I accept the{" "}
                      <a
                        onClick={(e) => {
                          e.preventDefault();
                          setIsTermsModalOpen(true);
                        }}
                        className="underline text-light-yellow hover:text-blue-800 cursor-pointer"
                      >
                        terms & conditions
                      </a>
                    </label>
                  </div>
                  <button
                    className="bg-[#FFBC00] text-[#F5F9FE] text-sm w-full p-3 rounded-lg mt-6 disabled:bg-light-yellow disabled:cursor-not-allowed"
                    disabled={selectedPlan.amount === 0 || !termsAccepted}
                    onClick={() => setShowPaymentForm(true)}
                  >
                    Proceed To Pay
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      {showPaymentForm && (
        <PaymentForm
          stripePromise={stripePromise}
          creditPlanId={selectedPlan?.id || 1}
          amount={selectedPlan?.discountedAmount || 0}
          isAutoPurchase={isAutoPurchaseEnabled}
          autoPurchaseCreditPlanId={autoPurchaseCreditPlanId}
          autoPurchaseCreditLimit={autoPurchaseThreshold}
          isMonthlyPurchase={isMonthlyPurchaseEnabled}
          monthlyPurchaseCreditPlanId={monthlyPurchaseCreditPlanId}
          onClose={handleCloseEverything}
          onBack={() => setShowPaymentForm(false)}
          onPaymentSuccess={() => {
            handleCloseEverything();
            onPaymentComplete();
          }}
        />
      )}
      {showConfirmModal && (
        <ConfirmationModal
          currentMode={purchaseMode}
          pendingMode={pendingMode}
          onClose={handleConfirmModalClose}
          onConfirm={handleConfirmModalConfirm}
          isSaving={isSaving}
          error={error}
        />
      )}
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        onAccept={() => {
          setTermsAccepted(true);
          setIsTermsModalOpen(false);
        }}
      />
    </>
  );
};

export default CreditPurchaseModal;
